/**
 * The robot bubble's one action: Pause while it works, Resume when paused,
 * Cancel task when its job is stuck.
 *
 * NO RULE OF ITS OWN. What is offered is `useTaskControlGates`, the hook the
 * task drawer's controls and the phone calendar's sheet read: the same roles,
 * the same capabilities, no Resume for a robot held after a fire alarm, no
 * Cancel while a stuck robot is in or at a lift car. Cancel asks first, as the
 * phone's does, and each press reads back the drawer's own verdict words, so
 * a command nothing answered never reads as done.
 */
import { useState } from 'react';
import { useTaskControl } from '../../../api/hooks';
import type { LiftViewModel } from '../../../api/adapters';
import type { RobotStateView, TaskState } from '../../../api/rmf-client';
import { hasAnyRole } from '../../../auth/roles';
import { useAuth } from '../../../auth/useAuth';
import { useActionsOff } from '../../../auth/useRolePreview';
import { TASK_VERDICT_CODE_TEXT } from '../../Tasks/task-command-verdict';
import {
  TASK_CONTROL_ROLES,
  taskControlOutcome,
  useTaskControlGates,
} from '../../Tasks/task-control-gates';
import { bubbleActionFor, type BubbleAction } from './bubble-action';

const ACTION_WORDS: Readonly<Record<BubbleAction, { press: string; done: string }>> = {
  interrupt: { press: 'Pause', done: 'Paused.' },
  resume: { press: 'Resume', done: 'Resumed.' },
  cancel: { press: 'Cancel task', done: 'Cancelled.' },
};

export function BubbleTaskAction({
  task,
  robot,
  lifts,
}: {
  task: TaskState;
  robot: RobotStateView;
  lifts: readonly LiftViewModel[] | undefined;
}) {
  const { roles, isAuthenticated } = useAuth();
  const actionsOff = useActionsOff();
  const control = useTaskControl();
  const gates = useTaskControlGates(task, robot, lifts);
  const [confirming, setConfirming] = useState(false);
  const outcome = taskControlOutcome(control, task.id);

  if (!isAuthenticated || !hasAnyRole(roles, TASK_CONTROL_ROLES)) return null;

  const action = bubbleActionFor(task, gates);
  // The word that came back is about the last press, whatever is offered now.
  const lastAction = control.variables?.action as BubbleAction | undefined;
  const entry = gates.availableActions.find((a) => a.action === action);
  const cap = entry ? gates.capabilityMap.get(entry.capability) : undefined;
  const capOff = gates.capabilitiesLoaded && cap?.enabled === false;
  const off = actionsOff || capOff || control.isPending;
  const title = actionsOff
    ? 'Viewing as another role: look only.'
    : capOff
      ? (cap?.disabled_reasons ?? []).join(', ')
      : entry?.hint;
  const send = (a: BubbleAction) => control.mutate({ taskId: task.id, action: a });
  const liftGuarded = action === 'cancel' && gates.guardedForLift;
  const pauseWithheld =
    action === null &&
    task.status === 'active' &&
    Boolean(gates.inLift) &&
    gates.availableActions.some((a) => a.action === 'interrupt');

  return (
    <div className="nx-map-bubble__task" data-testid="bubble-task-action">
      {gates.fireHeldBanner && (
        <p className="nx-map-bubble__note" role="alert" data-testid="bubble-fire-held">
          Held after the fire alarm. Open Details to release it.
        </p>
      )}
      {liftGuarded && (
        <p className="nx-map-bubble__note" role="alert" data-testid="bubble-in-lift">
          Don’t cancel while it is in or at {gates.inLift?.name ?? 'the lift'}. Call Nexsoss.
        </p>
      )}
      {pauseWithheld && (
        <p className="nx-map-bubble__note" data-testid="bubble-no-pause-in-lift">
          It can’t be paused from here while it is in or at {gates.inLift?.name ?? 'the lift'}.
        </p>
      )}
      {action && !liftGuarded && !confirming && (
        <button
          type="button"
          className={`nx-screen-btn${action === 'cancel' ? ' nx-map-bubble__danger' : ''}`}
          disabled={off}
          title={title}
          onClick={() => (action === 'cancel' ? setConfirming(true) : send(action))}
          data-testid={`bubble-action-${action}`}
        >
          {control.isPending ? 'Sending…' : `${ACTION_WORDS[action].press}${action === 'cancel' ? '…' : ''}`}
        </button>
      )}
      {action === 'cancel' && !liftGuarded && confirming && (
        <div className="nx-map-bubble__confirm" role="group" aria-label="Confirm cancel">
          <p className="nx-map-bubble__note">
            Cancel this task? The robot may finish a step first (for example, back out of a
            doorway) before it stops.
          </p>
          <div className="nx-map-bubble__buttons">
            <button
              type="button"
              className="nx-screen-btn"
              onClick={() => setConfirming(false)}
              data-testid="bubble-cancel-keep"
            >
              Keep it
            </button>
            <button
              type="button"
              className="nx-screen-btn nx-map-bubble__danger"
              disabled={off}
              onClick={() => {
                setConfirming(false);
                send('cancel');
              }}
              data-testid="bubble-cancel-confirm"
            >
              Cancel task
            </button>
          </div>
        </div>
      )}
      {outcome?.kind === 'pending' && (
        <p className="nx-map-bubble__note" role="status" data-testid="bubble-action-pending">
          Not confirmed yet. {outcome.message}
        </p>
      )}
      {outcome?.kind === 'verdict' && (
        <p
          className={`nx-map-bubble__note${outcome.status === 'done' ? '' : ' nx-map-bubble__note--fail'}`}
          role={outcome.status === 'done' ? 'status' : 'alert'}
          data-testid="bubble-action-verdict"
          data-status={outcome.status}
        >
          {outcome.status === 'done'
            ? lastAction && ACTION_WORDS[lastAction]
              ? ACTION_WORDS[lastAction].done
              : 'Done.'
            : (outcome.code && TASK_VERDICT_CODE_TEXT[outcome.code]) || outcome.reason}
        </p>
      )}
    </div>
  );
}
